import { Trans, useLingui } from '@lingui/react/macro';
import React, { useRef, useEffect, useLayoutEffect, useCallback, useMemo, useState } from 'react';
import { useMessageStore, useAuthStore, useChannelStore, useThreadStore } from '../stores';
import { useChannelMembers } from '../hooks/useChannelMembers';
import { dispatchOutgoingMessage } from '../stores/actions';
import { useTaskStore } from '../stores/useTaskStore';
import MessageItem from './MessageItem';
import HeartbeatCollapsed from './HeartbeatCollapsed';
import DirectMessageEmptyState from './DirectMessageEmptyState';
import { useMentionPushed } from '../hooks/useWsHubFrames';
import { markMessagePaint, observeMessageConversation } from '../lib/message-cache-session';

import type { Message, PendingMessage } from '../types';
import type { ChannelMember } from '../lib/api';
import type { MentionPushedFrame } from '../types/ws-frames';

const EMPTY_MESSAGES: Message[] = [];
const EMPTY_PENDING: PendingMessage[] = [];

/* ══════════════════════════════════════════════════════════════════════════
 * SCROLL MEMORY
 *
 * Module-level Map stores { distanceFromBottom, wasAtBottom } per channel.
 * Distance-from-bottom is used (rather than scrollTop) because it stays
 * stable regardless of content height changes above the viewport.
 *
 * On channel switch:
 *   1. Scroll listener continuously saves the OUTGOING channel's position
 *   2. useLayoutEffect restores the INCOMING channel's position before paint
 *   3. restoringRef suppresses the scroll listener during programmatic moves
 *
 * First visit / was-at-bottom → instant scroll to bottom (no animation).
 * Saved position → restore exact distance from bottom.
 * ══════════════════════════════════════════════════════════════════════ */

const scrollMemory = new Map<string, { distanceFromBottom: number; wasAtBottom: boolean }>();

/** Cap map size to prevent unbounded growth. */
const SCROLL_MAP_CAP = 200;
function scrollMemorySet(key: string, value: { distanceFromBottom: number; wasAtBottom: boolean }) {
  scrollMemory.set(key, value);
  if (scrollMemory.size > SCROLL_MAP_CAP) {
    const first = scrollMemory.keys().next().value;
    if (first !== undefined) scrollMemory.delete(first);
  }
}

function toPseudoMessage(p: PendingMessage): Message {
  return {
    id: p.clientMessageId,
    channel_id: p.channelId,
    sender_id: p.senderId,
    sender_name: p.senderName,
    content: p.content,
    content_type: p.contentType,
    reply_to_id: null,
    created_at: p.createdAt,
    edited_at: null,
    mentions: p.mentions,
    _pending: p.status === 'pending',
    _failed: p.status === 'failed',
    _clientMessageId: p.clientMessageId,
  };
}

interface Props {
  channelId: string;
  previewMessages?: Message[] | null;
  onThreadOpen?: (messageId: string) => void;
  activeThreadMessageId?: string;
  emptyTitle?: string;
  isDirectMessage?: boolean;
  directMessagePeer?: { id: string; display_name: string; avatar_url?: string | null; role?: string; state?: string };
  onStarterPrompt?: (prompt: string) => void;
}

export default function MessageList({ channelId, previewMessages, onThreadOpen, activeThreadMessageId, emptyTitle, isDirectMessage = false, directMessagePeer, onStarterPrompt }: Props) {
  const messages = useMessageStore(s => s.messages[channelId]) ?? EMPTY_MESSAGES;
  useEffect(() => observeMessageConversation(channelId), [channelId]);
  useLayoutEffect(() => { markMessagePaint(channelId, messages); }, [channelId, messages]);
  const hasMore = useMessageStore(s => s.hasMore[channelId]) ?? false;
  const loadingMessages = useMessageStore(s => s.loadingMessages[channelId] ?? false);
  const pendingMessages = useMessageStore(s => s.pendingMessages[channelId]) ?? EMPTY_PENDING;
  const loadMessages = useMessageStore(s => s.loadMessages);
  const loadOlderMessages = useMessageStore(s => s.loadOlderMessages);
  const currentUser = useAuthStore(s => s.currentUser);
  const { t } = useLingui();
  const channelName = useChannelStore(s => s.channels.find(c => c.id === channelId)?.name ?? emptyTitle ?? t`channel`);
  const removePendingMessage = useMessageStore(s => s.removePendingMessage);
  const containerRef = useRef<HTMLDivElement>(null);
  const bottomRef = useRef<HTMLDivElement>(null);
  const prevScrollHeight = useRef(0);
  const isAtBottom = useRef(true);
  const isInitialLoad = useRef(true);
  const loadingOlder = useRef(false);
  /** Suppresses scroll-save during programmatic position changes. */
  const restoringRef = useRef(false);
  /** Tracks channelId for the scroll handler (avoids stale closures). */
  const channelIdRef = useRef(channelId);
  const [showNewMsgBtn, setShowNewMsgBtn] = useState(false);
  const { members } = useChannelMembers(channelId);
  const loadThreads = useThreadStore(s => s.loadThreads);
  const loadChannelTasks = useTaskStore(s => s.loadChannelTasks);
  const prevMessageCount = useRef(0);

  const allMessagesData = previewMessages ?? messages;
  const pending = previewMessages ? [] : pendingMessages;
  const allMessages = [...allMessagesData, ...pending.map(toPseudoMessage)];
  const isLoading = loadingMessages;

  useEffect(() => {
    loadThreads(channelId);
    loadChannelTasks(channelId);
  }, [channelId, loadThreads, loadChannelTasks]);

  // A MentionPushedFrame is only a signal to refresh the channel so the
  // mentioned message appears promptly. The complete body comes from
  // actions.loadMessages, while body_preview is trimmed
  // to 80 runes server-side (TruncateBodyPreview) and intentionally
  // discarded here rather than parsed or displayed.
  const onMentionPushed = useCallback((frame: MentionPushedFrame) => {
    if (frame.channel_id !== channelId) return;
    if (!currentUser || frame.mention_target_id !== currentUser.id) return;
    void loadMessages(channelId);
  }, [channelId, currentUser, loadMessages]);
  useMentionPushed(onMentionPushed);

  const userMap = useMemo(() => {
    const map = new Map<string, string>();
    for (const member of members) map.set(member.user_id, member.display_name);
    if (currentUser) map.set(currentUser.id, currentUser.display_name);
    for (const msg of allMessages) {
      if (msg.sender_name) map.set(msg.sender_id, msg.sender_name);
    }
    return map;
  }, [members, currentUser, allMessages]);

  const memberMap = useMemo(() => {
    const map = new Map<string, ChannelMember>();
    for (const member of members) map.set(member.user_id, member);
    return map;
  }, [members]);

  // ── Scroll position restore on channel switch (useLayoutEffect = before paint) ──
  useLayoutEffect(() => {
    channelIdRef.current = channelId;
    isInitialLoad.current = true;
    setShowNewMsgBtn(false);
  }, [channelId]);

  useLayoutEffect(() => {
    const container = containerRef.current;
    if (!container || allMessages.length === 0) return;
    if (!isInitialLoad.current) return;

    isInitialLoad.current = false;
    const mem = scrollMemory.get(channelId);
    restoringRef.current = true;

    if (!mem || mem.wasAtBottom) {
      // First visit or was at bottom → snap to bottom instantly
      container.scrollTop = container.scrollHeight;
      isAtBottom.current = true;
    } else {
      // Restore saved position (distance from bottom)
      container.scrollTop = container.scrollHeight - mem.distanceFromBottom - container.clientHeight;
      isAtBottom.current = false;
    }

    // Clear restoring flag after browser processes the scroll
    requestAnimationFrame(() => {
      restoringRef.current = false;
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [channelId, allMessages.length]);

  // ── Auto-scroll on new messages + floating button ──
  // Combined into one effect so prevMessageCount is read before being updated.
  useEffect(() => {
    if (isInitialLoad.current) {
      prevMessageCount.current = allMessages.length;
      return;
    }
    if (allMessages.length > prevMessageCount.current) {
      // Check if last message is own (pending)
      const lastMsg = allMessages[allMessages.length - 1];
      const isOwn = lastMsg && (lastMsg._pending || lastMsg._clientMessageId);
      if (isOwn || isAtBottom.current) {
        bottomRef.current?.scrollIntoView({ behavior: 'smooth' });
      } else {
        // User is scrolled up — show floating "new messages" button
        setShowNewMsgBtn(true);
      }
    }
    prevMessageCount.current = allMessages.length;
  }, [allMessages.length]);

  // ── Preserve scroll position when prepending older messages ──
  useLayoutEffect(() => {
    if (loadingOlder.current && containerRef.current) {
      const newScrollHeight = containerRef.current.scrollHeight;
      const diff = newScrollHeight - prevScrollHeight.current;
      if (diff > 0) {
        restoringRef.current = true;
        containerRef.current.scrollTop += diff;
        requestAnimationFrame(() => {
          restoringRef.current = false;
        });
      }
      loadingOlder.current = false;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [allMessages.length]);

  const scrollToBottom = useCallback(() => {
    bottomRef.current?.scrollIntoView({ behavior: 'smooth' });
    setShowNewMsgBtn(false);
  }, []);

  const handleScroll = useCallback(() => {
    const container = containerRef.current;
    if (!container) return;

    // Skip saving during programmatic scroll (restore / prepend)
    if (restoringRef.current) return;

    const { scrollTop, scrollHeight, clientHeight } = container;
    const distFromBottom = scrollHeight - scrollTop - clientHeight;
    const atBottom = distFromBottom < 50;
    isAtBottom.current = atBottom;

    // Persist scroll position for this channel
    scrollMemorySet(channelIdRef.current, {
      distanceFromBottom: distFromBottom,
      wasAtBottom: atBottom,
    });

    if (atBottom) {
      setShowNewMsgBtn(false);
    }

    // Load older messages when scrolled to top
    if (scrollTop < 100 && hasMore && !isLoading) {
      loadingOlder.current = true;
      prevScrollHeight.current = scrollHeight;
      loadOlderMessages(channelId);
    }
  }, [channelId, hasMore, isLoading, loadOlderMessages]);

  const handleRetry = useCallback((msg: Message) => {
    if (!msg._clientMessageId) return;
    // `Message.content_type` is wider than the `contentType` the outbound path
    // accepts. Turning away the content types that path has no case for leaves
    // a value that type-checks against it; naming those rather than re-listing
    // the accepted ones keeps this line from becoming one more copy of the
    // accepted set. Add an inbound-only type and this assignment stops
    // compiling, which is where the retry question wants deciding. A row
    // turned away here keeps whatever the timeline already shows for it.
    const contentType = msg.content_type;
    if (contentType === 'tool_card' || contentType === 'epoch') return;
    // The failed row makes way for the fresh attempt, so retrying a message
    // leaves one row in the timeline rather than two.
    removePendingMessage(msg._clientMessageId, channelId);
    dispatchOutgoingMessage({
      channelId,
      content: msg.content,
      contentType,
      mentions: msg.mentions,
    });
  }, [channelId, removePendingMessage]);

  return (
    <div
      className="message-list"
      ref={containerRef}
      onScroll={handleScroll}
    >
      {hasMore && (
        <div className="load-more">
          {isLoading ? (
            <span className="loading-spinner"><Trans>Loading…</Trans></span>
          ) : (
            <button
              className="btn btn-sm"
              onClick={() => loadOlderMessages(channelId)}
            >
              <Trans>Load earlier messages</Trans>
            </button>
          )}
        </div>
      )}

      {!hasMore && messages.length > 0 && (
        <div className="no-more-messages"><Trans>This is the beginning of the conversation</Trans></div>
      )}

      {isLoading && allMessages.length === 0 && (
        <div className="message-skeleton">
          {[1, 2, 3].map(i => (
            <div key={i} className="skeleton-item">
              <div className="skeleton-avatar" />
              <div className="skeleton-body">
                <div className="skeleton-line skeleton-short" />
                <div className="skeleton-line skeleton-long" />
              </div>
            </div>
          ))}
        </div>
      )}

      {!isLoading && allMessages.length === 0 && (isDirectMessage && directMessagePeer
        ? <DirectMessageEmptyState userId={directMessagePeer.id} displayName={directMessagePeer.display_name} avatarUrl={directMessagePeer.avatar_url} role={directMessagePeer.role} state={directMessagePeer.state} onStarterPrompt={onStarterPrompt} />
        : <div className="empty-channel"><h2># {channelName}</h2><p className="empty-hint"><Trans>This is the beginning of the #{channelName} channel.</Trans></p></div>
      )}

      {(() => {
        const items: React.ReactNode[] = [];
        let i = 0;
        while (i < allMessages.length) {
          const msg = allMessages[i];
          const isHeartbeat = msg.type === 'task_heartbeat';
          if (isHeartbeat) {
            let j = i + 1;
            while (j < allMessages.length && allMessages[j].type === 'task_heartbeat') {
              j++;
            }
            const count = j - i;
            if (count > 1) {
              items.push(
                <HeartbeatCollapsed key={`hb-group-${allMessages[i].id}`} count={count} messages={allMessages.slice(i, j)} />
              );
            } else {
              items.push(
                <MessageItem
                  key={msg.id}
                  message={msg}
                  userMap={userMap}
                  members={members}
                  memberMap={memberMap}
                  currentUserId={currentUser?.id}
                  currentUserRole={currentUser?.role}
                  onRetry={msg._failed ? handleRetry : undefined}
                  onThreadOpen={onThreadOpen}
                  isThreadActive={msg.id === activeThreadMessageId}
                />
              );
            }
            i = j;
          } else {
            items.push(
              <MessageItem
                key={msg.id}
                message={msg}
                userMap={userMap}
                members={members}
                memberMap={memberMap}
                currentUserId={currentUser?.id}
                currentUserRole={currentUser?.role}
                onRetry={msg._failed ? handleRetry : undefined}
                onThreadOpen={onThreadOpen}
                isThreadActive={msg.id === activeThreadMessageId}
              />
            );
            i++;
          }
        }
        return items;
      })()}

      <div ref={bottomRef} />

      {showNewMsgBtn && (
        <button className="new-message-btn" onClick={scrollToBottom}>
          <Trans>New messages</Trans>
        </button>
      )}
    </div>
  );
}
